import React, { useState, useEffect } from 'react';
import { Send, Clock, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { fetchCustomers, createOrderForm, fetchOrderForms } from '../lib/api';
import type { Customer, Contact, OrderForm } from '../lib/types';

const OrderForm: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [formData, setFormData] = useState<{
    customerId: string;
    contactId: string;
    email: string;
  }>({
    customerId: '',
    contactId: '',
    email: ''
  });

  const [generatedLink, setGeneratedLink] = useState('');
  const [pendingOrders, setPendingOrders] = useState<OrderForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCustomer = customers.find(c => c.id === formData.customerId);
  const selectedContact = selectedCustomer?.contacts.find(c => c.id === formData.contactId);
  const canSubmit = selectedCustomer?.licenseNumber && selectedCustomer?.taxId;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedContact) {
      setFormData(prev => ({
        ...prev,
        email: selectedContact.email
      }));
    }
  }, [formData.contactId, selectedContact]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [customersData, orderFormsData] = await Promise.all([
        fetchCustomers(),
        fetchOrderForms()
      ]);
      setCustomers(customersData);
      setPendingOrders(orderFormsData);
      setError(null);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.customerId || !formData.contactId || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    if (!canSubmit) {
      setError('Cannot send order form: Customer must have both License Number and Tax ID set');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await createOrderForm(formData);
      
      if (!response || !response.id) {
        throw new Error('Invalid response from server');
      }
      
      setPendingOrders([response, ...pendingOrders]);
      setGeneratedLink(`${window.location.origin}/view-order/${response.id}`);
      
      // Reset form
      setFormData({
        customerId: '',
        contactId: '',
        email: ''
      });
    } catch (error) {
      console.error('Error creating order form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create order form';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: OrderForm['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'viewed':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: OrderForm['status']) => {
    switch (status) {
      case 'pending':
        return Clock;
      case 'viewed':
        return CheckCircle;
      case 'expired':
        return AlertCircle;
      default:
        return Clock;
    }
  };

  const formatTimeAgo = (date: string) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const formatTimeLeft = (expiresAt: string) => {
    const seconds = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        {error}
        <button
          onClick={loadData}
          className="block mx-auto mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Send Order Form</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <select
              value={formData.customerId}
              onChange={(e) => setFormData({ 
                ...formData, 
                customerId: e.target.value,
                contactId: '',
                email: ''
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            >
              <option value="">Select a company</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.companyName}
                </option>
              ))}
            </select>
          </div>

          {selectedCustomer && (
            <>
              {!canSubmit && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Missing Required Information
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          This customer cannot receive order forms until the following information is provided:
                        </p>
                        <ul className="list-disc list-inside mt-1">
                          {!selectedCustomer.licenseNumber && (
                            <li>License Number</li>
                          )}
                          {!selectedCustomer.taxId && (
                            <li>Tax ID</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Person
                </label>
                <select
                  value={formData.contactId}
                  onChange={(e) => setFormData({ ...formData, contactId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
                  required
                >
                  <option value="">Select a contact</option>
                  {selectedCustomer.contacts.map(contact => (
                    <option key={contact.id} value={contact.id}>
                      {contact.firstName} {contact.lastName} - {contact.position}
                    </option>
                  ))}
                </select>

                {selectedContact && (
                  <div className="bg-gray-50 p-4 rounded-md">
                    <div className="font-medium text-gray-900">
                      {selectedContact.firstName} {selectedContact.lastName}
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedContact.position}
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedContact.email}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting || !formData.customerId || !formData.contactId || !canSubmit}
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            <span>{isSubmitting ? 'Generating...' : 'Generate Order Form Link'}</span>
          </button>
        </form>

        {generatedLink && (
          <div className="mt-8 p-4 bg-green-50 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-2">Order Form Link Generated!</h3>
            <p className="text-sm text-gray-600 mb-4">
              An email has been sent to {formData.email} with the following link:
            </p>
            <div className="bg-white p-3 rounded border border-green-200">
              <a
                href={generatedLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 break-all"
              >
                {generatedLink}
              </a>
            </div>
          </div>
        )}
      </div>

      {pendingOrders.length > 0 && (
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Pending Order Forms</h3>
          <div className="space-y-4">
            {pendingOrders.map((order) => {
              const StatusIcon = getStatusIcon(order.status);
              return (
                <div key={order.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-3">
                        <h4 className="font-medium text-gray-900">{order.customerName}</h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Contact: {order.contactName}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                        <span>Sent {formatTimeAgo(order.sentAt)}</span>
                        {order.status === 'viewed' && order.lastViewed && (
                          <span>Last viewed {formatTimeAgo(order.lastViewed)}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        Expires in {formatTimeLeft(order.expiresAt)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderForm;